// src/pages/FundPlanner/reports/exportPortfolioPositionsPdf.js

import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addReportTable } from "reporting/pdf/addReportTable";
import { addKeyValueBlock } from "reporting/pdf/pdfLayout";
import { safeFilePart } from "reporting/pdf/pdfFormatters";
import { PDF_STONE } from "reporting/pdf/pdfTheme";
import { buildPortfolioPositionsReportData } from "./buildPortfolioPositionsReportData";

const PERIOD_COLUMN_WIDTHS = {
  level_name: 86,
  settlement_date: 32,
  cusip_or_id: 46,
  issuer_name: 48,
  rating_rollup: 22,
  amount: 41,
  coupon: 27,
  maturity_date: 32,
  wam_maturity_days: 29,
  first_call_date: 32,
  last_price: 28,
  market_value: 41,
  purchase_price: 28,
  original_cost: 41,
  unrealized_gain_loss: 39,
  accrued_interest: 39,
  book_value: 41,
  net_income: 39,
  ytm: 25,
};

const MONEY_COLUMN_KEYS = new Set([
  "amount",
  "market_value",
  "original_cost",
  "unrealized_gain_loss",
  "accrued_interest",
  "book_value",
  "net_income",
]);

const POSITION_ROW_STYLES = {
  fillColor: PDF_STONE.white,
  lineColor: PDF_STONE.border,
  lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 },
};

const SPACER_ROW_STYLES = {
  fillColor: PDF_STONE.white,
  lineWidth: 0,
  cellPadding: 0,
  minCellHeight: 12,
};

const GROUP_ROW_STYLES = {
  portfolio: {
    fillColor: PDF_STONE.primary,
    textColor: PDF_STONE.white,
    lineColor: PDF_STONE.white,
    lineWidth: { top: 1.8, right: 0, bottom: 0, left: 0 },
    cellPadding: { top: 2.4, right: 1.2, bottom: 2.4, left: 1.2 },
    minCellHeight: 10.5,
  },
  fund_type: {
    fillColor: PDF_STONE.secondary,
    textColor: PDF_STONE.white,
    lineColor: PDF_STONE.white,
    lineWidth: { top: 1.4, right: 0, bottom: 0, left: 0 },
    cellPadding: { top: 2, right: 1.2, bottom: 2, left: 1.2 },
    minCellHeight: 9.5,
  },
  fund: {
    fillColor: PDF_STONE.surface,
    textColor: PDF_STONE.primaryDark,
    lineColor: PDF_STONE.secondaryLight,
    lineWidth: { top: 0.7, right: 0, bottom: 0.25, left: 0 },
    cellPadding: { top: 1.8, right: 1.2, bottom: 1.8, left: 1.2 },
    minCellHeight: 8.5,
  },
};

function addHierarchySpacers(table) {
  const emptyCells = Object.fromEntries(table.columns.map((column) => [column.key, ""]));
  const rows = table.rows.flatMap((row) => {
    if (row.row_type !== "fund_type" && row.row_type !== "fund") return [row];

    return [{ ...emptyCells, row_type: "spacer" }, row];
  });

  return { ...table, rows };
}

function getColumnStyles(columns, reportMode) {
  const widths = { ...PERIOD_COLUMN_WIDTHS };

  // Single-date reports omit Net Income. Reuse that space for the two
  // text-heavy columns instead of allowing the table to become narrower.
  if (reportMode !== "period") {
    widths.level_name += 25;
    widths.issuer_name += 14;
  }

  return Object.fromEntries(
    columns.map((column, index) => [
      index,
      {
        cellWidth: widths[column.key],
        ...(MONEY_COLUMN_KEYS.has(column.key) ? { overflow: "hidden" } : {}),
      },
    ])
  );
}

function styleGroupedRows(rows) {
  return (hookData) => {
    if (hookData.section !== "body") return;

    const row = rows[hookData.row.index];
    if (!row) return;

    if (row.row_type === "spacer") {
      Object.assign(hookData.cell.styles, SPACER_ROW_STYLES);
      return;
    }

    if (row.row_type === "position") {
      Object.assign(hookData.cell.styles, POSITION_ROW_STYLES);
      return;
    }

    const groupStyles = GROUP_ROW_STYLES[row.row_type];
    if (!groupStyles) return;

    Object.assign(hookData.cell.styles, groupStyles, { fontStyle: "bold" });
  };
}

export function renderPortfolioPositionsPdf(ctx, data) {
  const { margins } = ctx;
  const positionsTable = addHierarchySpacers(data.positionsTable);
  let y = margins.top + 2;

  y = addKeyValueBlock(
    ctx,
    [
      { label: "Portfolio", value: data.meta.portfolioName || "Current Portfolio" },
    ],
    y,
    {
      columns: 1,
      labelWidth: 58,
      rowHeight: 14,
      spaceAfter: 2,
    }
  );

  addReportTable(
    ctx,
    {
      ...positionsTable,
      fontSize: 5.2,
      columnStyles: getColumnStyles(positionsTable.columns, data.meta.reportMode),
      styles: {
        cellPadding: 1.2,
        overflow: "linebreak",
        minCellHeight: 7,
      },
      headStyles: {
        fontSize: 5.2,
        cellPadding: 1.2,
        fillColor: PDF_STONE.primary,
        textColor: PDF_STONE.white,
      },
      titleOptions: {
        spaceBefore: 8,
        spaceAfter: 8,
      },
      rowPageBreak: "avoid",
      alternateRowStyles: {
        fillColor: PDF_STONE.white,
      },
      didParseCell: styleGroupedRows(positionsTable.rows),
    },
    y
  );
}

export async function exportPortfolioPositionsPdf({
  reportData = null,
  save = true,
  ...builderArgs
} = {}) {
  const data = reportData || buildPortfolioPositionsReportData(builderArgs);

  const ctx = await createCompassPdf({
    orgId: builderArgs.orgId,
    reportTitle: data.meta.reportTitle,
    orgName: data.meta.orgName || data.meta.portfolioName,
    asOfLabel: data.meta.asOfLabel,
    createdStamp: new Date(),
    orientation: "landscape",
    branding: "unbranded",
    contentMargins: {
      top: 102,
      bottom: 58,
    },
  });

  renderPortfolioPositionsPdf(ctx, data);

  ctx.finalize();

  const { doc } = ctx;

  const fileName = `FundPlanner_Positions_${safeFilePart(
    data.meta.orgName || data.meta.portfolioName || "Portfolio"
  )}_${safeFilePart(data.meta.portfolioName || "Positions")}_${dayjs(
    data.meta.fileDate
  ).format("YYYY-MM-DD")}.pdf`;

  if (save) {
    doc.save(fileName);
  }

  return {
    doc,
    fileName,
    reportData: data,
  };
}

export default exportPortfolioPositionsPdf;
